import React, { useState } from 'react';
import { Row, Col, Card, Typography, Form, Input, Button, message, Divider } from 'antd';
import { 
  PhoneOutlined, 
  MailOutlined, 
  WhatsAppOutlined, 
  ClockCircleOutlined,
  SendOutlined,
  UserOutlined,
  MessageOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const Contact: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Thank you for your message! We will get back to you soon.');
      form.resetFields();
    } catch (error) {
      message.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Title level={1} style={{ color: 'white', marginBottom: '10px' }}>
            Contact Us
          </Title>
          <Paragraph style={{ color: 'white', fontSize: '1.1rem' }}>
            Get in touch with our team for all your microfinance needs
          </Paragraph>
        </div>
      </div>

      {/* Contact Information */}
      <div style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2}>Get in Touch</Title>
            <Paragraph style={{ fontSize: '1.1rem', color: '#666' }}>
              We're here to help you with all your financial needs. Contact us through any of the channels below.
            </Paragraph>
          </div>
          
          <Row gutter={[30, 30]}>
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', height: '100%' }}>
                <PhoneOutlined style={{ fontSize: '3rem', color: '#1890ff', marginBottom: '20px' }} />
                <Title level={4}>Phone</Title>
                <Paragraph>
                  Call us for immediate assistance with your loan inquiries and applications.
                </Paragraph>
                <Text strong style={{ fontSize: '1.3rem', color: '#1890ff' }}>08158120510</Text>
                <div style={{ marginTop: '15px' }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PhoneOutlined />}
                    href="tel:08158120510"
                    style={{
                      background: '#1890ff',
                      borderColor: '#1890ff',
                      fontSize: '0.9rem'
                    }}
                  >
                    Call Now
                  </Button>
                </div>
                <div style={{ marginTop: '10px' }}>
                  <Text type="secondary">Available Sunday - Friday: 8:00 AM - 6:00 PM</Text>
                </div>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', height: '100%' }}>
                <MailOutlined style={{ fontSize: '3rem', color: '#52c41a', marginBottom: '20px' }} />
                <Title level={4}>Email</Title>
                <Paragraph>
                  Send us your questions and we'll respond within 24 hours.
                </Paragraph>
                <Text strong style={{ fontSize: '1.2rem', color: '#52c41a' }}>fairlonerhelp@gmail.com</Text>
                <div style={{ marginTop: '15px' }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<MailOutlined />}
                    href="mailto:fairlonerhelp@gmail.com"
                    style={{
                      background: '#52c41a',
                      borderColor: '#52c41a',
                      fontSize: '0.9rem'
                    }}
                  >
                    Send Email
                  </Button>
                </div>
                <div style={{ marginTop: '10px' }}>
                  <Text type="secondary">We respond to all emails promptly</Text>
                </div>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', height: '100%' }}>
                <WhatsAppOutlined style={{ fontSize: '3rem', color: '#25D366', marginBottom: '20px' }} />
                <Title level={4}>WhatsApp</Title>
                <Paragraph>
                  Chat with us on WhatsApp for quick support and instant responses.
                </Paragraph>
                <Text strong style={{ fontSize: '1.3rem', color: '#25D366' }}>+234 815 812 0510</Text>
                <div style={{ marginTop: '15px' }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<WhatsAppOutlined />}
                    href="https://wa.me/2348158120510"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: '#25D366',
                      borderColor: '#25D366',
                      fontSize: '0.9rem'
                    }}
                  >
                    Chat Now
                  </Button>
                </div>
                
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Contact Form */}
      <div style={{ background: '#f8f9fa', padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[40, 40]} align="top">
            <Col xs={24} lg={12}>
              <Title level={2}>Send us a Message</Title>
              <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '30px' }}>
                Have a question about our services or need assistance with your loan application? 
                Fill out the form below and we'll get back to you as soon as possible.
              </Paragraph>
              
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="large"
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[{ required: true, message: 'Please enter your first name' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Your first name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[{ required: true, message: 'Please enter your last name' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Your last name" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter your email address' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="your.email@example.com" />
                </Form.Item>
                
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, message: 'Please enter your phone number' }]}
                >
                  <Input prefix={<PhoneOutlined />} placeholder="Your phone number" />
                </Form.Item>
                
                <Form.Item
                  name="subject"
                  label="Subject"
                  rules={[{ required: true, message: 'Please enter a subject' }]}
                >
                  <Input placeholder="What is this about?" />
                </Form.Item>
                
                <Form.Item
                  name="message"
                  label="Message"
                  rules={[{ required: true, message: 'Please enter your message' }]}
                >
                  <TextArea 
                    rows={6} 
                    placeholder="Please describe your inquiry or question in detail..."
                    showCount
                    maxLength={1000}
                  />
                </Form.Item>
                
                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    size="large"
                    loading={loading}
                    icon={<SendOutlined />}
                    style={{ width: '100%' }}
                  >
                    Send Message
                  </Button>
                </Form.Item>
              </Form>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card style={{ height: '100%' }}>
                <Title level={3} style={{ marginBottom: '30px' }}>
                  <MessageOutlined style={{ marginRight: '10px', color: '#1890ff' }} />
                  Why Contact Us?
                </Title>
                
                <div style={{ marginBottom: '25px' }}>
                  <Title level={4} style={{ color: '#1890ff', marginBottom: '10px' }}>
                    Loan Inquiries
                  </Title>
                  <Paragraph>
                    Get information about our loan products, eligibility requirements, 
                    interest rates, and application process.
                  </Paragraph>
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <Title level={4} style={{ color: '#52c41a', marginBottom: '10px' }}>
                    Application Support
                  </Title>
                  <Paragraph>
                    Need help with your loan application? Our team can guide you through 
                    the process and answer any questions you may have.
                  </Paragraph>
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <Title level={4} style={{ color: '#722ed1', marginBottom: '10px' }}>
                    Account Management
                  </Title>
                  <Paragraph>
                    Existing customers can get assistance with account management, 
                    payment schedules, and loan modifications.
                  </Paragraph>
                </div>
                
                <div style={{ marginBottom: '25px' }}>
                  <Title level={4} style={{ color: '#fa8c16', marginBottom: '10px' }}>
                    General Information
                  </Title>
                  <Paragraph>
                    Learn more about our services, policies, and how we can help you 
                    achieve your financial goals.
                  </Paragraph>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Business Hours */}
      <div style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} lg={12}>
              <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                <ClockCircleOutlined style={{ fontSize: '4rem', color: '#1890ff', marginBottom: '20px' }} />
                <Title level={3}>Business Hours</Title>
                <Paragraph style={{ fontSize: '1.1rem' }}>
                  Our customer service team is available during these hours to assist you 
                  with all your inquiries and loan applications.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Title level={2}>When Can You Reach Us?</Title>
              <div style={{ background: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
                <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                  <div style={{ 
                    background: '#1890ff', 
                    color: 'white', 
                    padding: '15px 20px', 
                    borderRadius: '8px',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    Monday - Sunday
                  </div>
                  <div style={{ 
                    fontSize: '1.4rem', 
                    color: '#1890ff', 
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}>
                    8:00 AM - 6:00 PM
                  </div>
                  <div style={{ color: '#666', fontSize: '1rem' }}>
                    Available 7 days a week for your convenience
                  </div>
                </div>
                
                <Divider />
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <Text strong style={{ fontSize: '1.1rem', color: '#52c41a' }}>
                      ✓ Extended Hours
                    </Text>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <Text strong style={{ fontSize: '1.1rem', color: '#52c41a' }}>
                      ✓ Weekend Support
                    </Text>
                  </div>
                  <div>
                    <Text strong style={{ fontSize: '1.1rem', color: '#52c41a' }}>
                      ✓ Consistent Service
                    </Text>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      
    </div>
  );
};

export default Contact;
